import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react'
import PropTypes from 'prop-types'
import { Section } from '../../02-Molecules/Section/Section'
import { AddressLookUp } from "../../02-Molecules/AddressLookUp/AddressLookUp";
import {FormCheckbox} from "../../01-Atoms/FormCheckbox/FormCheckbox";

/**
 * SectionAddress component.
 */
export const SectionAddress = forwardRef(({ name, collapsed, heading, headingLarge, loqateKey, populateFrom, sectionId, tracking, trackingEvent, validationHash, values, textBefore, textAfter, ...props }, ref) => {
  const [shouldPopulateFromField, shouldPopulateFromFieldSet] = useState(false)
  const [refreshKey, refreshKeySet] = useState(Date.now())
  const fields = ['_address_line_1', '_address_line_2', '_address_town_city', '_address_post_code']

  const isValid = () => {
    // If the input name is not `recipient_address`.
    if (name !== 'recipient_address') {
      // Don't perform validation: return as valid.
      return true;
    }
    else {
      // Generate field names to check.
      return fields
        .filter((field) => field !== '_address_line_2') // Remove address line 2, because it's not mandatory
        .map((field) => `${name}${field}`) // Generate field names based on {name} prop
        .reduce((isValid, key) => values[key] === '' ? false : isValid, true); // Set section validity based on section field values.
    }
  }

  /**
   * Override validation check.
   */
  useImperativeHandle(ref, () => ({ isValid: () => isValid() }))

  const handleShouldPopulateChange = (name, value) => {
    shouldPopulateFromFieldSet(value)
  }

  useEffect(() => {
    fields.forEach(field => {
      const fieldValue = shouldPopulateFromField ? values[`${populateFrom.field}${field}`] : ''
      props.onInputChange(`${name}${field}`, fieldValue)
    })

    refreshKeySet(Date.now())
  }, [shouldPopulateFromField])

  return (
    <Section collapsed={collapsed} heading={heading} headingLarge={headingLarge} sectionId={sectionId} textBefore={textBefore} textAfter={textAfter}>

      {populateFrom &&
        <FormCheckbox name={`${name}_populate_from`} onInputChange={handleShouldPopulateChange} checked={shouldPopulateFromField} label={populateFrom.label} />
      }

      <AddressLookUp
        key={refreshKey}
        loqateKey={loqateKey}
        legend={'Address details'}
        names={{
          addressLine1: `${name}_address_line_1`,
          addressLine2: `${name}_address_line_2`,
          townCity: `${name}_address_town_city`,
          postCode: `${name}_address_post_code`,
        }}
        showLookup={!shouldPopulateFromField}
        onInputChange={props.onInputChange}
        tracking={tracking}
        trackEvent={trackingEvent}
        validationHash={validationHash}
        values={{
          addressLine1: shouldPopulateFromField ? values[`${populateFrom.field}_address_line_1`]    : values[`${name}_address_line_1`],
          addressLine2: shouldPopulateFromField ? values[`${populateFrom.field}_address_line_2`]    : values[`${name}_address_line_2`],
          townCity    : shouldPopulateFromField ? values[`${populateFrom.field}_address_town_city`] : values[`${name}_address_town_city`],
          postCode    : shouldPopulateFromField ? values[`${populateFrom.field}_address_post_code`] : values[`${name}_address_post_code`],
        }}/>

    </Section>
  )
})


/**
 * SectionAddress PropTypes.
 */
SectionAddress.propTypes = {
  name           : PropTypes.string,
  loqateKey      : PropTypes.string,
  collapsed      : PropTypes.bool,
  heading        : PropTypes.string,
  headingLarge   : PropTypes.bool,
  sectionId      : PropTypes.string,
  validationHash : PropTypes.string,
  populateFrom   : PropTypes.shape({
    field: PropTypes.string,
    label: PropTypes.string,
  }),
  onInputChange: PropTypes.func,
  tracking     : PropTypes.shape({
    id  : PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    name: PropTypes.string,
    type: PropTypes.oneOf(['entry', 'exit', 'entry_exit'])
  }),
  trackEvent: PropTypes.func,
  values    : PropTypes.object
}
